'use client'

import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  JSX,
} from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { PartialNetworkPill } from '~/components/PartialNetworkPill'
import { withAuthentication } from '~/components/auth/withAuthentication'
import {
  Button,
  EmptyState,
  List,
  Message,
  NetworkDisplay,
  PageLayout,
  Toggle,
} from '~/components/component-library'
import {
  SettingsPageContainer,
  settingsVisibility,
} from '~/components/settings/utils'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useUpdateOrgNetworks } from '~/hooks/merkle-transactions/useUpdateOrgNetworks'
import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { useBalanceTotalsByChain } from '~/hooks/useBalanceTotalsByChain'
import { useOrgBalances } from '~/hooks/useBalances'
import { useNavigation } from '~/hooks/useNavigation'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useAllChainIds } from '~/hooks/utils'
import { AuthOrg } from '~/types'
import { areSetsEquals } from '~/utils'
import { EvmAddress } from '~/utils/address'
import {
  getChainLabel,
  properlyFormatOneOrMoreChainNames,
} from '~/utils/chainLabels'
import { displayCurrencyShorthand } from '~/utils/display'

const TeamNetworksPageInner = () => {
  const {
    onboarding: { isOnboarding },
  } = useAuth()
  const navigate = useNavigation()
  const { smartAccounts } = useSmartAccounts()

  return (
    <SettingsPageContainer
      requireOwner={settingsVisibility.networks === 'owners'}
    >
      <PageLayout
        pageType="standard"
        title="Networks"
        description={
          <span>
            Choose the networks where your workspace’s accounts are available.{' '}
            <SplitsLink
              path="https://splits.org/docs/introduction/networks-and-assets"
              target="tab"
              underline
            >
              Learn more
            </SplitsLink>
            .
          </span>
        }
      >
        {smartAccounts.length > 0 ? (
          <OrgSmartAccountsNetworksEdit />
        ) : (
          <EmptyState>
            {isOnboarding ? (
              <div className="flex flex-col items-center space-y-4">
                <div>Complete setup to get started</div>
                <Button
                  onClick={() => {
                    navigate('/')
                  }}
                >
                  Complete setup
                </Button>
              </div>
            ) : (
              <div>
                No accounts found. Set up accounts to manage networks. If this
                seems to be a mistake, contact support at support@splits.org
              </div>
            )}
          </EmptyState>
        )}
      </PageLayout>
    </SettingsPageContainer>
  )
}

const OrgSmartAccountsNetworksEdit = (): JSX.Element => {
  const { createUpdateOrgNetworksTransaction, isProcessing } =
    useUpdateOrgNetworks()
  const org = useActiveOrg().org!
  const activeOrgId = useActiveOrgId()
  const queryClient = useQueryClient()
  const orgNetworkIds = useMemo(
    () => new Set(org.smartAccountsChainIds),
    [org.smartAccountsChainIds],
  )

  const listedChainIds = useAllChainIds()
  const [selectedChainIds, setSelectedChainIds] =
    useState<Set<number>>(orgNetworkIds)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setIsSaving(false)
  }, [orgNetworkIds])

  const { balances } = useOrgBalances(org.id)
  const { smartAccounts } = useSmartAccounts()

  const detectedByChainId = useBalanceTotalsByChain({
    addresses: smartAccounts.map((account) =>
      EvmAddress.format(account.address),
    ),
    chainIds: listedChainIds.filter((chainId) => !orgNetworkIds.has(chainId)),
  })

  const discoveredChainIds = listedChainIds.filter(
    (chainId) => (detectedByChainId[chainId] ?? 0) > 0,
  )
  const totalDetected = discoveredChainIds.reduce(
    (acc, chainId) => acc + detectedByChainId[chainId],
    0,
  )

  const unselectedChainIds = useMemo(
    () =>
      new Set(
        [...orgNetworkIds].filter((chainId) => !selectedChainIds.has(chainId)),
      ),
    [orgNetworkIds, selectedChainIds],
  )

  const balancesOnChainsToBeDisabled = useMemo(
    () => getBalancesPerChain(balances, Array.from(unselectedChainIds)),
    [balances, unselectedChainIds],
  )

  const valueOfAssetsAtRisk = balancesOnChainsToBeDisabled.reduce(
    (acc, { balance }) => acc + balance,
    0,
  )

  const valueOfAssetsAtRiskFormatted =
    displayCurrencyShorthand(valueOfAssetsAtRisk)

  const onSelectNetwork = (chainId: number, enabled: boolean) => {
    setSelectedChainIds((chainIds) => {
      const copy = new Set(chainIds)
      if (enabled) {
        copy.add(chainId)
      } else {
        copy.delete(chainId)
      }
      return copy
    })
  }

  const hasChanges = !areSetsEquals(orgNetworkIds, selectedChainIds)
  const isCurrentlyProcessing = isProcessing || isSaving
  const canSave = hasChanges && !isCurrentlyProcessing

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      setIsSaving(true)

      createUpdateOrgNetworksTransaction({
        orgId: org.id,
        chainIds: [...selectedChainIds],
        onError: () => {
          setIsSaving(false)
        },
        onSuccess: () => {
          queryClient.setQueryData(
            [QUERY_KEYS.ACTIVE_ORG, activeOrgId],
            (old: AuthOrg) => {
              return {
                ...old,
                smartAccountsChainIds: [...selectedChainIds],
              }
            },
          )
        },
      })
    },
    [
      selectedChainIds,
      createUpdateOrgNetworksTransaction,
      org.id,
      queryClient,
      activeOrgId,
    ],
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {discoveredChainIds.length > 0 && (
        <Message variant="info">
          <span>
            You have ~{displayCurrencyShorthand(totalDetected)} on{' '}
            {properlyFormatOneOrMoreChainNames(
              discoveredChainIds.map((chainId) => getChainLabel(chainId)),
            )}
            . Enable{' '}
            {discoveredChainIds.length > 1 ? 'these networks' : 'this network'}{' '}
            to access those assets.
          </span>
        </Message>
      )}
      <List
        items={listedChainIds.map((chainId) => ({
          key: `${chainId}`,
          content: (
            <span className="flex items-center gap-2">
              <NetworkDisplay layout="standard" chainId={chainId} />
              <PartialNetworkPill chainId={chainId} />
            </span>
          ),
          meta: discoveredChainIds.includes(chainId)
            ? `${displayCurrencyShorthand(detectedByChainId[chainId])} detected`
            : undefined,
          onClick: () =>
            onSelectNetwork(chainId, !selectedChainIds.has(chainId)),
          actionIcon: (
            <Toggle isChecked={selectedChainIds.has(chainId)} size="base" />
          ),
        }))}
        footer="Your accounts can only receive assets on supported networks. If you receive assets on a network not listed here, reach out at support@splits.org."
      />
      {unselectedChainIds.size > 0 && valueOfAssetsAtRisk > 0 && (
        <Message variant="danger">
          <span>
            Warning: you own ~{valueOfAssetsAtRiskFormatted} in tokens on{' '}
            {properlyFormatOneOrMoreChainNames(
              Array.from(unselectedChainIds)
                .filter((chainId) =>
                  balancesOnChainsToBeDisabled.some(
                    (balance) => balance.chainId === chainId,
                  ),
                )
                .map((chainId) => getChainLabel(chainId)),
            )}{' '}
            that will be inaccessible while{' '}
            {unselectedChainIds.size > 1
              ? 'these networks are'
              : 'this network is'}{' '}
            disabled. You can access these assets at any time by re-enabling{' '}
            {unselectedChainIds.size > 1 ? 'these networks' : 'this network'}.
          </span>
        </Message>
      )}
      <div className="flex items-center justify-between space-x-4 pt-4">
        <div>
          {isCurrentlyProcessing && hasChanges ? (
            <div className="flex items-center text-sm">
              <AlertCircle className="mr-1.5 h-4 w-4" />
              Saving changes...
            </div>
          ) : hasChanges ? (
            <div className="flex items-center text-sm text-orange-500">
              <AlertCircle className="mr-1.5 h-4 w-4" />
              Unsaved changes
            </div>
          ) : null}
        </div>
        <Button
          type="submit"
          eventName="clickedEditNetwork"
          eventProperties={{ orgId: org.id }}
          isDisabled={!canSave}
          disabledReason={
            isCurrentlyProcessing && hasChanges
              ? 'Saving changes...'
              : undefined
          }
          isLoading={isCurrentlyProcessing}
        >
          Save
        </Button>
      </div>
    </form>
  )
}

type OrgBalances = ReturnType<typeof useOrgBalances>['balances']

function getBalancesPerChain(
  balances: OrgBalances,
  chainIds?: number[],
): { chainId: number; balance: number }[] {
  const flatBalances = Object.values(balances).flatMap(
    (account) => account.balances,
  )

  const totals = flatBalances.reduce<Record<number, number>>((acc, entry) => {
    acc[entry.chainId] = (acc[entry.chainId] || 0) + (entry?.usdValue ?? 0)
    return acc
  }, {})

  const balancesPerChain = Object.entries(totals).map(([chainId, balance]) => ({
    chainId: Number(chainId),
    balance,
  }))

  if (chainIds) {
    return balancesPerChain.filter((balance) =>
      chainIds.includes(balance.chainId),
    )
  }
  return balancesPerChain
}
export const TeamNetworksPage = withAuthentication(TeamNetworksPageInner)
